/**
 * OTP Resend Limiter
 * Manages OTP resend local limit using localStorage
 * Local limit: X minutes between each resend (same as OTP expiry time)
 */

import moment from 'moment'
import { OTP_EXPIRY_TIME } from '@common'
import logger from './logger'

const OTP_RESEND_STORAGE_KEY = 'otp_resend_limiter'
const LOCAL_LIMIT_SECONDS = OTP_EXPIRY_TIME // same as OTP expiry time

/**
 * Check if OTP can be resent for the given email
 * @param {string} email - email address
 * @returns {Object} { canResend, localLimitEnd }
 */
export const checkResendLimit = email => {
    let stored = {}
    try {
        const storedData = localStorage.getItem(OTP_RESEND_STORAGE_KEY)
        if (storedData) {
            stored = JSON.parse(storedData)
        }
    } catch (error) {
        // If localStorage is disabled or JSON.parse fails, treat as no limit
        logger.warn('Failed to read OTP resend limit from localStorage:', error)
        return {
            canResend: true,
            localLimitEnd: null,
        }
    }
    const now = Date.now()

    // Different email, reset
    if (stored.email !== email) {
        return {
            canResend: true,
            localLimitEnd: null,
        }
    }

    // Check local limit period
    if (stored.lastResendTime) {
        const localLimitEnd = stored.lastResendTime + LOCAL_LIMIT_SECONDS * 1000
        if (now < localLimitEnd) {
            return {
                canResend: false,
                localLimitEnd,
            }
        }
    }

    return {
        canResend: true,
        localLimitEnd: null,
    }
}

/**
 * Record a resend attempt for the given email
 * @param {string} email - email address
 * @returns {Object} updated resend data
 */
export const recordResendAttempt = email => {
    const now = Date.now()
    const newData = {
        email,
        lastResendTime: now,
    }
    try {
        localStorage.setItem(OTP_RESEND_STORAGE_KEY, JSON.stringify(newData))
    } catch (error) {
        // If localStorage is disabled or quota exceeded, silently fail
        logger.warn('Failed to save OTP resend limit to localStorage:', error)
    }
    return newData
}

/**
 * Clear resend limit data (for testing or manual reset)
 * @param {string} email - optional email to clear specific email data
 */
export const clearResendLimit = email => {
    try {
        if (email) {
            const storedData = localStorage.getItem(OTP_RESEND_STORAGE_KEY)
            if (storedData) {
                const stored = JSON.parse(storedData)
                if (stored.email === email) {
                    localStorage.removeItem(OTP_RESEND_STORAGE_KEY)
                }
            }
        } else {
            localStorage.removeItem(OTP_RESEND_STORAGE_KEY)
        }
    } catch (error) {
        // If localStorage is disabled or JSON.parse fails, silently fail
        logger.warn('Failed to clear OTP resend limit from localStorage:', error)
    }
}

/**
 * Calculate time left considering all priorities
 * Priority: nextAvailableTime > localLimit > OTP expiry
 * @param {string|Date|null} nextAvailableTime - Rate limit from backend
 * @param {Object} limit - Resend limit object from checkResendLimit
 * @param {number|null} otpExpiry - OTP expiry timestamp
 * @returns {number} Time left in seconds
 */
export const calculateTimeLeft = (nextAvailableTime, limit, otpExpiry) => {
    // Priority 1: nextAvailableTime (rate limit from backend)
    if (nextAvailableTime) {
        const nextAvailableMoment = moment(nextAvailableTime)
        if (nextAvailableMoment.isValid() && nextAvailableMoment.isAfter(moment())) {
            return Math.max(0, Math.floor((nextAvailableMoment.valueOf() - moment().valueOf()) / 1000))
        }
        // If nextAvailableTime exists but is invalid or expired, continue to next priority
    }
    // Priority 2: localLimit (local storage limit)
    if (limit.localLimitEnd) {
        return Math.max(0, Math.floor((limit.localLimitEnd - Date.now()) / 1000))
    }
    // Priority 3: OTP expiry
    if (otpExpiry) {
        return Math.max(0, Math.floor((otpExpiry - Date.now()) / 1000))
    }
    return OTP_EXPIRY_TIME
}

/**
 * Update resend state based on current limits
 * @param {string} email - Email address
 * @param {string|Date|null} nextAvailableTime - Rate limit from backend
 * @param {number|null} otpExpiry - OTP expiry timestamp
 * @returns {Object} { limit, canResend, timeLeft }
 */
export const updateResendState = (email, nextAvailableTime, otpExpiry) => {
    const limit = checkResendLimit(email)
    const nextAvailableMoment = nextAvailableTime ? moment(nextAvailableTime) : null
    const isRateLimited = nextAvailableMoment && nextAvailableMoment.isValid() && nextAvailableMoment.isAfter(moment())
    return {
        limit,
        canResend: limit.canResend && !isRateLimited,
        timeLeft: calculateTimeLeft(nextAvailableTime, limit, otpExpiry),
    }
}
